import { useApolloClient } from '@apollo/client/react'
import { useLanguage, useTranslate } from '@athena/ui'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { AuthScreen } from '../features/auth/AuthScreen.js'
import { safeReturnPath } from '../lib/return-to.js'
import { SignInPanel } from '../features/auth/SignInPanel.js'

/**
 * Sign in. The invitation half of the same flow lives at /invite, which needs
 * the extra step of claiming a student row once the session exists.
 *
 * ── Why the cache is reset on the way in ────────────────────────────────
 *
 * This screen is where a second person signs in on a machine the first was
 * using. The first person's `me`, students and practices may still be in
 * Apollo's cache — signing out clears it, but an expired session, a revoked
 * one, or a browser restored from a tab group does not. Clearing before
 * navigating means the app that renders after sign-in has nothing of theirs
 * left to read.
 */
export function SignInScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const navigate = useNavigate()
  const client = useApolloClient()
  // `?error=` is set by the API when a provider round trip failed, and mapped
  // to a key by `providerErrorKey`; never printed raw. `?redirect=` is where a
  // protected screen was sending the visitor, already validated by the route.
  const search = useSearch({ from: '/$language/sign-in' })
  const returnTo = safeReturnPath(search.redirect)

  return (
    // One title for both kinds of visitor: a code sign-in with a new address
    // creates the account, and onboarding asks what kind it is. "Start for
    // free" on the public site lands here, so the heading has to say that
    // this is where an account is made, not only where one is used.
    <AuthScreen title={t('auth.signInTitle')} subtitle={t('auth.signInSubtitle')}>
      <SignInPanel
        {...(search.error ? { providerError: search.error } : {})}
        // A provider comes back to the same page the code would.
        {...(returnTo ? { callbackPath: returnTo } : {})}
        onSignedIn={async () => {
          await client.clearStore()
          // Back where they were going, in the language they are reading now.
          // A new account's layout sends it on to onboarding from there.
          if (returnTo) await navigate({ href: `/${language}${returnTo}`, replace: true })
          else await navigate({ to: '/$language', params: { language }, replace: true })
        }}
      />
    </AuthScreen>
  )
}
